import type { PoolRejoin } from '@otryad/shared';
import { useState } from 'react';

import { useHaptic } from '@/app/TmaProvider';
import { Button, Card } from '@/components/ui';
import { apiReason } from '@/lib/links';
import { errorText, fmt, texts } from '@/lib/texts';
import { trpc } from '@/lib/trpc';

import { sizeLabel } from '../routes/onboarding/ActivitiesScreen';

const t = texts.pool;

/**
 * «Вернуться в набор» (раунд 4, N1). Список приходит с сервера (`pool.status.rejoin`) — по снятым
 * заявкам, а не из памяти экрана, поэтому пункт есть и на `/pool`, и в профиле, сколько бы человек
 * ни уходил с экрана. Возврат — один `pool.join` по активности и размеру: вечера, районы и уровень
 * сервер берёт из профиля, пять вопросов заново не нужны.
 */
export function RejoinCard({
  items,
  slotsLeft,
  primary,
}: {
  items: readonly PoolRejoin[];
  slotsLeft: number;
  /** Главное действие экрана (своих наборов нет) — зелёная кнопка, иначе второстепенная */
  primary: boolean;
}) {
  const haptic = useHaptic();
  const utils = trpc.useUtils();
  const join = trpc.pool.join.useMutation();
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (items.length === 0) return null;

  const rejoin = async (item: PoolRejoin) => {
    setError(null);
    setPending(item.activity.id);
    try {
      await join.mutateAsync({ activityId: item.activity.id, squadSize: item.squadSize });
      haptic.notify('success');
      await Promise.all([
        utils.pool.invalidate(),
        utils.auth.me.invalidate(),
        utils.profile.get.invalidate(),
      ]);
    } catch (e) {
      haptic.notify('error');
      setError(errorText(apiReason(e)));
    } finally {
      setPending(null);
    }
  };

  return (
    <Card title={t.rejoinTitle}>
      <p className="text-muted mb-3 text-sm">{slotsLeft > 0 ? t.rejoinBody : t.rejoinLimit}</p>
      <div className="flex flex-col gap-2">
        {items.map((item) => (
          <Button
            key={item.activity.id}
            fullWidth
            variant={primary ? 'primary' : 'secondary'}
            disabled={slotsLeft <= 0 || (pending !== null && pending !== item.activity.id)}
            loading={pending === item.activity.id}
            onClick={() => void rejoin(item)}
          >
            {fmt(t.rejoinItem, { activity: item.activity.name, size: sizeLabel(item.squadSize) })}
          </Button>
        ))}
      </div>
      {error ? (
        <p className="text-danger mt-2 text-sm" role="alert">
          {error}
        </p>
      ) : null}
    </Card>
  );
}
